import { Box, Fade, Slide, Stack } from '@mui/material';
import { useState } from 'react';
import { AppAlert } from './AppAlert';
import { AlertEntry } from '../../TypeScript/interfaces';
import { FADEOUT_TIME } from '../constants';

export interface AppAlertQueueProps {
  list: AlertEntry[];
  removeItem: (id: string) => void;
}

const VISIBLE_ALERT_LIMIT = 2;
const PEEK_ALERT_HEIGHT = 20;
const ALERT_WIDTH = 450;
const ALERT_SX_PADDING = 48;

export const AppAlertQueue = ({ list, removeItem }: AppAlertQueueProps) => {
  const [exitingAlertIds, setExitingAlertIds] = useState<Set<string>>(
    () => new Set(),
  );

  const visibleAlerts = list.slice(0, VISIBLE_ALERT_LIMIT);

  if (visibleAlerts.length === 0) {
    // Nothing to render
    return null;
  }

  /**
   * Handles the closing of an alert by marking it as exiting
   * and triggering its exit animation.
   *
   * @param alertId The ID of the alert that is being closed.
   */
  const handleCloseAlert = (alertId: string) => {
    setExitingAlertIds((currentIds) => {
      if (currentIds.has(alertId)) {
        return currentIds;
      }

      const nextIds = new Set(currentIds);
      nextIds.add(alertId);
      return nextIds;
    });
  };

  /**
   * Handles removing the alert from the exiting alert IDs
   * and the main alert list once its exit animation is complete.
   *
   * @param alertId The ID of the alert that has finished exiting.
   */
  const handleAlertExited = (alertId: string) => {
    removeItem(alertId);
    setExitingAlertIds((currentIds) => {
      if (!currentIds.has(alertId)) {
        return currentIds;
      }

      const nextIds = new Set(currentIds);
      nextIds.delete(alertId);
      return nextIds;
    });
  };

  return (
    <Box
      sx={{
        bottom: 8,
        left: 8,
        position: 'fixed',
        width: {
          xs: `calc(100vw - ${ALERT_SX_PADDING}px)`,
          sm: ALERT_WIDTH,
        },
        zIndex: (theme) => theme.zIndex.snackbar,
      }}
    >
      <Stack spacing={0}>
        {visibleAlerts.map((alert, index) => {
          const isExiting = exitingAlertIds.has(alert.id);
          const isPeekAlert = index === 1;

          return (
            <Box
              key={alert.id}
              sx={{
                transform: isPeekAlert
                  ? `translateY(${PEEK_ALERT_HEIGHT}px)`
                  : 'translateY(0)',
                transition: `transform ${FADEOUT_TIME}ms ease`,
              }}
            >
              <Fade
                in={!isExiting}
                timeout={FADEOUT_TIME}
                unmountOnExit
                onExited={() => handleAlertExited(alert.id)}
              >
                <Slide direction="up" in timeout={FADEOUT_TIME}>
                  <Box>
                    <AppAlert
                      appAlert={alert}
                      isQueueHead={index === 0}
                      onClose={() => handleCloseAlert(alert.id)}
                    />
                  </Box>
                </Slide>
              </Fade>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};
